import type { ProjectAnnotation } from '@/types/project-annotations';
import type { PreviewAnnotationTarget } from '@/lib/annotations/preview-annotation-target';
import { resolveAnnotationTargetCategory } from '@/lib/annotations/annotation-filters';

function readAnnotationTargetValue(
  annotation: ProjectAnnotation,
  key: keyof NonNullable<ProjectAnnotation['metadata']['annotationTarget']>
): string | null {
  const value = annotation.metadata.annotationTarget?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function readPreviewTargetValue(
  target: PreviewAnnotationTarget,
  key: keyof NonNullable<ProjectAnnotation['metadata']['annotationTarget']>
): string | null {
  const value = target.targetMetadata.annotationTarget?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

export function getAnnotationStoryboardId(annotation: ProjectAnnotation): string | null {
  return (
    annotation.targetStoryboardId ??
    annotation.metadata.annotationTarget?.storyboardId ??
    (annotation.targetType === 'storyboard' ? annotation.targetId : null)
  );
}

export function buildUnreadAnnotationStoryboardCounts(
  annotations: readonly ProjectAnnotation[]
): ReadonlyMap<string, number> {
  const counts = new Map<string, number>();

  for (const annotation of annotations) {
    if (!annotation.isUnread) continue;
    if (resolveAnnotationTargetCategory(annotation) !== 'storyboard') continue;
    const storyboardId = getAnnotationStoryboardId(annotation);
    if (!storyboardId) continue;
    counts.set(storyboardId, (counts.get(storyboardId) ?? 0) + 1);
  }

  return counts;
}

export function getPreviewAnnotationTargetUnreadKey(
  target: PreviewAnnotationTarget | null | undefined
): string | null {
  if (!target?.targetType || !target.targetId) return null;
  return `${target.targetType}:${target.targetId}`;
}

function getAnnotationTargetUnreadKey(annotation: ProjectAnnotation): string | null {
  if (!annotation.targetType || !annotation.targetId) return null;
  return `${annotation.targetType}:${annotation.targetId}`;
}

export function buildUnreadAnnotationTargetCounts(
  annotations: readonly ProjectAnnotation[]
): ReadonlyMap<string, number> {
  const counts = new Map<string, number>();

  for (const annotation of annotations) {
    if (!annotation.isUnread) continue;
    const key = getAnnotationTargetUnreadKey(annotation);
    if (!key) continue;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  return counts;
}

function hasSharedMetadataId(
  annotation: ProjectAnnotation,
  target: PreviewAnnotationTarget,
  keys: Array<keyof NonNullable<ProjectAnnotation['metadata']['annotationTarget']>>
): boolean {
  return keys.some((key) => {
    const annotationValue = readAnnotationTargetValue(annotation, key);
    const targetValue = readPreviewTargetValue(target, key);
    return Boolean(annotationValue && targetValue && annotationValue === targetValue);
  });
}

function readIdentityPart(value: string | null | undefined): string | null {
  const text = typeof value === 'string' ? value.trim() : '';
  return text || null;
}

type StoryboardVideoIdentitySource = {
  targetId?: string | null;
  targetStoryboardId?: string | null;
  planId?: string | null;
  previewItemId?: string | null;
  entityId?: string | null;
};

function resolveStoryboardVideoIdentity(source: StoryboardVideoIdentitySource): string | null {
  const planId = readIdentityPart(source.planId);
  const storyboardId = readIdentityPart(source.targetStoryboardId);
  const candidates = [
    readIdentityPart(source.previewItemId),
    readIdentityPart(source.entityId),
    readIdentityPart(source.targetId),
  ];
  for (const id of candidates) {
    if (id && id !== planId && id !== storyboardId) return id;
  }
  const targetId = readIdentityPart(source.targetId);
  if (targetId && targetId === storyboardId) return targetId;
  return null;
}

export function getStoryboardVideoAnnotationIdentity(
  annotation: Pick<ProjectAnnotation, 'targetId' | 'targetStoryboardId' | 'metadata'>
): string | null {
  return resolveStoryboardVideoIdentity({
    targetId: annotation.targetId,
    targetStoryboardId: annotation.targetStoryboardId,
    planId: annotation.metadata.annotationTarget?.planId,
    previewItemId: annotation.metadata.annotationTarget?.previewItemId,
    entityId: annotation.metadata.annotationTarget?.entityId,
  });
}

export function getPreviewStoryboardVideoIdentity(target: PreviewAnnotationTarget): string | null {
  return resolveStoryboardVideoIdentity({
    targetId: target.targetId,
    targetStoryboardId: target.targetStoryboardId,
    planId: target.targetMetadata.annotationTarget?.planId,
    previewItemId: target.targetMetadata.annotationTarget?.previewItemId,
    entityId: target.targetMetadata.annotationTarget?.entityId,
  });
}

export function isSamePreviewAnnotationTarget(
  annotation: ProjectAnnotation,
  target: PreviewAnnotationTarget
): boolean {
  if (annotation.targetType !== target.targetType) return false;

  if (annotation.targetType === 'storyboard_video' || target.targetType === 'storyboard_video') {
    const annotationVideoId = getStoryboardVideoAnnotationIdentity(annotation);
    const targetVideoId = getPreviewStoryboardVideoIdentity(target);
    return Boolean(annotationVideoId && targetVideoId && annotationVideoId === targetVideoId);
  }

  if (annotation.targetId && annotation.targetId === target.targetId) return true;

  return hasSharedMetadataId(annotation, target, [
    'storyboardId',
    'resourceId',
    'tagId',
    'planId',
    'previewItemId',
    'entityId',
  ]);
}

export function getPreviewAnnotationTargetUnreadCount(
  target: PreviewAnnotationTarget | null | undefined,
  annotations: readonly ProjectAnnotation[]
): number {
  if (!target) return 0;
  return annotations.filter(
    (annotation) => annotation.isUnread && isSamePreviewAnnotationTarget(annotation, target)
  ).length;
}
